import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useLiveMatchChannel from "../live/useLiveMatchChannel";

const STORAGE_ROOT = "elitlig-live-v2";
const BROADCAST_CHANNEL = "elitlig-live-match";
const API_POLL_INTERVAL = 15000;
const LOCAL_POLL_INTERVAL = 5000;

const parseJson = (value, fallback = null) => {
  if (value == null) return fallback;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return fallback;
  }
};

const toMilliseconds = (value, fallbackText = "") => {
  if (Number.isFinite(value)) {
    return Math.max(0, Number(value));
  }
  if (value == null && fallbackText) {
    return toMilliseconds(fallbackText);
  }
  if (value == null) return 0;
  const str = String(value).trim();
  if (!str) return 0;
  const match = str.match(/^(\d{1,3})(?::(\d{1,2}))?$/);
  if (match) {
    const minutes = parseInt(match[1], 10);
    const seconds = match[2] ? parseInt(match[2], 10) : 0;
    if (!Number.isNaN(minutes) && !Number.isNaN(seconds)) {
      return minutes * 60 * 1000 + Math.min(59, Math.max(0, seconds)) * 1000;
    }
  }
  const numeric = Number(str.replace(/[^\d.]/g, ""));
  if (Number.isFinite(numeric)) {
    // Eğer saniye olarak geldiyse yuvarla
    return Math.max(0, Math.round(numeric));
  }
  return 0;
};

const formatClock = (ms) => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60)
    .toString()
    .padStart(2, "0");
  const seconds = (totalSeconds % 60).toString().padStart(2, "0");
  return `${minutes}:${seconds}`;
};

const formatLiveMinute = (ms) => {
  const minute = Math.ceil(Math.max(0, Number(ms) || 0) / 60000) || 1;
  return `${minute}'`;
};

const normaliseEventType = (value) => {
  const str = String(value || "").toLowerCase();
  if (str.includes("yellow")) return "yellow";
  if (str.includes("red")) return "red";
  if (str.includes("sub")) return "substitution";
  if (str.includes("assist")) return "assist";
  return "goal";
};

const parseClockForSort = (value, text = "") => {
  if (Number.isFinite(value)) return Number(value);
  const str = String(value ?? text ?? "").trim();
  if (!str) return 0;
  const mmss = str.match(/^(\d{1,3})(?::(\d{1,2}))?$/);
  if (mmss) {
    const minutes = parseInt(mmss[1], 10);
    const seconds = mmss[2] ? parseInt(mmss[2], 10) : 0;
    if (!Number.isNaN(minutes) && !Number.isNaN(seconds)) {
      return minutes * 60 * 1000 + Math.min(59, Math.max(0, seconds)) * 1000;
    }
  }
  const minuteLike = str.match(/^(\d{1,3})(?:\+(\d{1,2}))?/);
  if (minuteLike) {
    const base = parseInt(minuteLike[1], 10) || 0;
    const extra = parseInt(minuteLike[2] || "0", 10) || 0;
    return (base + extra) * 60 * 1000;
  }
  const numeric = parseInt(str.replace(/[^\d]/g, ""), 10);
  if (Number.isFinite(numeric)) {
    return numeric * 60 * 1000;
  }
  return 0;
};

const normaliseEvents = (value) => {
  const parsed = parseJson(value, []);
  const list = Array.isArray(parsed) ? parsed : [];
  return list
    .map((item, index) => {
      if (!item) return null;
      const clockText =
        item.clockText ||
        item.clock_text ||
        item.time ||
        item.minuteLabel ||
        item.minute ||
        "";
      const clockMs = toMilliseconds(item.clockMs ?? item.clock_ms, clockText);
      return {
        id: item.id || `event-${index}`,
        type: normaliseEventType(item.type || item.event_type),
        team: item.team === "away" ? "away" : "home",
        playerId: item.playerId || item.player_id || "",
        playerName: item.playerName || item.player_name || item.player || "",
        secondaryPlayerId: item.secondaryPlayerId || item.secondary_player_id || "",
        secondaryPlayerName:
          item.secondaryPlayerName || item.secondary_player_name || item.assist_player || "",
        note: item.note || item.description || "",
        clockText: clockText || formatClock(clockMs),
        clockMs,
        createdAt: item.createdAt || item.created_at || null,
        score: item.score || null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      const diff = parseClockForSort(a.clockMs, a.clockText) - parseClockForSort(b.clockMs, b.clockText);
      if (diff !== 0) return diff;
      return String(a.createdAt || "").localeCompare(String(b.createdAt || ""));
    });
};

const deriveScoreFromEvents = (events) => {
  if (!Array.isArray(events) || events.length === 0) {
    return { home: 0, away: 0, hasGoals: false };
  }
  let home = 0;
  let away = 0;
  events.forEach((event) => {
    if (event?.type !== "goal") return;
    if (event.team === "away") {
      away += 1;
      return;
    }
    home += 1;
  });
  return { home, away, hasGoals: home + away > 0 };
};

const resolveScore = (snapshot, team, fallback) => {
  const candidates = team === "home"
    ? [
        snapshot.homeScore,
        snapshot.first_team_score,
        snapshot.home_score,
        snapshot.live_home_score,
        snapshot.live_first_team_score,
        snapshot?.scoreboard?.homeScore,
        snapshot?.scoreboard?.home,
        snapshot?.scoreboard?.Home,
        snapshot?.score?.home,
        snapshot?.score?.Home,
        snapshot?.HomeScore,
      ]
    : [
        snapshot.awayScore,
        snapshot.second_team_score,
        snapshot.away_score,
        snapshot.live_away_score,
        snapshot.live_second_team_score,
        snapshot?.scoreboard?.awayScore,
        snapshot?.scoreboard?.away,
        snapshot?.scoreboard?.Away,
        snapshot?.score?.away,
        snapshot?.score?.Away,
        snapshot?.AwayScore,
      ];

  for (const value of candidates) {
    if (value === undefined || value === null || value === "") continue;
    const num = Number(value);
    if (Number.isFinite(num)) return num;
  }
  return fallback;
};

const resolvePeriod = (snapshot, fallback) => {
  const candidates = [snapshot.period, snapshot.live_period, snapshot.periodText, snapshot.phase];
  for (const value of candidates) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  return fallback;
};

const resolveRunning = (snapshot, prevRunning) => {
  if (snapshot.isRunning !== undefined) return !!snapshot.isRunning;
  if (typeof snapshot.running === "boolean") return snapshot.running;

  if (snapshot.live_timer_state) {
    const timerState = String(snapshot.live_timer_state).toLowerCase();
    if (["finished", "ended", "bitti", "fulltime", "ft", "paused", "break", "halftime", "devre"].includes(timerState)) {
      return false;
    }
    if (["running", "live", "playing", "inplay", "active"].includes(timerState)) {
      return true;
    }
  }

  if (snapshot.live_timer_running !== undefined) {
    const value = snapshot.live_timer_running;
    if (typeof value === "string") return ["true", "1", "yes", "running"].includes(value.toLowerCase());
    if (typeof value === "number") return value !== 0;
    return !!value;
  }

  const status = String(
    snapshot.live_status || snapshot.status || snapshot.match_status || snapshot.mac_durumu || ""
  ).toLowerCase();
  if (["finished", "ended", "bitti", "fulltime", "ft"].some((token) => status.includes(token))) {
    return false;
  }
  if (["live", "canli", "inplay", "playing", "active", "ongoing"].some((token) => status.includes(token))) {
    return true;
  }

  if (snapshot.live_time || snapshot.clock || snapshot.clockMs || snapshot.clock_ms) {
    const period = String(snapshot.period || snapshot.live_period || snapshot.phase || "").toLowerCase();
    if (["finished", "ended", "bitti", "fulltime", "ft"].some((token) => period.includes(token))) {
      return false;
    }
    if (["devre", "half", "break", "paused"].some((token) => period.includes(token))) {
      return prevRunning;
    }
    return true;
  }

  return prevRunning;
};

const resolveUpdatedAt = (snapshot, fallback) => {
  const candidates = [
    snapshot.updatedAt,
    snapshot.updated_at,
    snapshot.live_timer_updated_at,
    snapshot.lastUpdated,
    snapshot.lastSyncedAt,
    snapshot.pendingSync?.lastTriedAt,
  ];
  for (const value of candidates) {
    if (value === undefined || value === null || value === "") continue;
    const num = Number(value);
    if (Number.isFinite(num)) return num;
    const parsed = Date.parse(String(value));
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
};

const makeStorageKey = (matchId, city) => {
  if (!matchId) return "";
  const cityKey = city ? String(city).trim() : "unknown";
  return `${STORAGE_ROOT}:${cityKey}::${matchId}`;
};

const convertLocalToSnapshot = (localData, matchId) => {
  if (!localData || typeof localData !== "object") return null;
  const snapshot = {
    matchId,
    homeScore: localData?.scoreboard?.homeScore,
    awayScore: localData?.scoreboard?.awayScore,
    period: localData?.scoreboard?.period,
    clockMs: localData?.scoreboard?.clockMs,
    isRunning: localData?.scoreboard?.isRunning,
    updatedAt: localData?.scoreboard?.updatedAt,
    live_events: localData?.events,
    playerStats: localData?.playerStats,
  };

  const buildLineup = (side) => {
    const ids = Array.isArray(localData?.lineups?.[side]) ? localData.lineups[side] : [];
    const roster = Array.isArray(localData?.rosters?.[side]) ? localData.rosters[side] : [];
    if (ids.length > 0 && roster.length > 0) {
      return ids
        .map((id, index) => {
          const found = roster.find((player) => String(player.id) === String(id));
          if (!found) return null;
          const name = found.name || found.player_name || "";
          if (!name) return null;
          const image =
            found.player_img ||
            found.image ||
            found.playerImage ||
            found.photo ||
            found.avatar ||
            "";
          return {
            id: found.id || id || `${side}-lineup-${index}`,
            name: name,
            number: found.number ?? found.jersey_number ?? null,
            image,
          };
        })
        .filter(Boolean);
    }
    if (roster.length > 0) {
      return roster
        .map((player, index) => {
          const name = player.name || player.player_name || "";
          if (!name) return null;
          const image =
            player.player_img ||
            player.image ||
            player.playerImage ||
            player.photo ||
            player.avatar ||
            "";
          return {
            id: player.id || `${side}-lineup-${index}`,
            name,
            number: player.number ?? player.jersey_number ?? null,
            image,
          };
        })
        .filter(Boolean);
    }
    return [];
  };

  const homeLineup = buildLineup("home");
  const awayLineup = buildLineup("away");
  if (homeLineup.length) snapshot.live_home_lineup = homeLineup;
  if (awayLineup.length) snapshot.live_away_lineup = awayLineup;

  return snapshot;
};

const initialFeedState = {
  events: [],
  lastEvent: null,
  homeScore: null,
  awayScore: null,
  period: "",
  clockMs: 0,
  running: false,
  updatedAt: 0,
  playerStats: null,
  source: null,
  matchStatus: "",
};

const isAbortError = (error) => {
  if (!error) return false;
  return (
    error.name === "AbortError" ||
    error.code === "ERR_CANCELED" ||
    String(error.message || "").toLowerCase().includes("aborted")
  );
};

export default function useLiveMatchFeed(matchId) {
  const channel = useLiveMatchChannel(matchId, { tickTimer: true });
  const snapshot = channel.match;
  // Olay listesi (ayrıştırma + sıralama) yalnızca kanaldaki olaylar değişince
  // yeniden hesaplanır; saniyede birkaç kez gelen sayaç tikleri bunu tetiklemez.
  const { events, derivedScore } = useMemo(() => {
    const list = normaliseEvents(channel.events);
    return { events: list, derivedScore: deriveScoreFromEvents(list) };
  }, [channel.events]);
  const homeScore = Math.max(Number(channel.score?.home) || 0, derivedScore.hasGoals ? derivedScore.home : 0);
  const awayScore = Math.max(Number(channel.score?.away) || 0, derivedScore.hasGoals ? derivedScore.away : 0);
  const clockMs = channel.timerMs || 0;
  return useMemo(() => ({
    events,
    lastEvent: events[events.length - 1] || null,
    homeScore,
    awayScore,
    period: channel.timer?.period ?? snapshot?.live_period ?? 1,
    clockMs,
    clockText: formatClock(clockMs),
    minuteText: formatLiveMinute(clockMs),
    running: Boolean(channel.timer?.running),
    updatedAt: channel.lastUpdatedAt,
    playerStats: snapshot?.playerStats || {},
    source: channel.isSocketConnected ? "socket" : "snapshot",
    loading: Boolean(matchId) && !snapshot,
    error: channel.error ? "Maç verisi alınamadı." : "",
    snapshot: snapshot ? { data: snapshot, source: channel.isSocketConnected ? "socket" : "api", receivedAt: channel.lastUpdatedAt } : null,
    connectionStatus: channel.connectionStatus,
    isSocketConnected: channel.isSocketConnected,
    isFallbackPolling: channel.isFallbackPolling,
    refreshSnapshot: channel.refreshSnapshot,
  }), [events, homeScore, awayScore, clockMs, channel, matchId, snapshot]);
}
